import { BreakpointConfig, HookBreakpointName } from '@todesktop/shared';

export interface BreakpointDefinition {
  config: BreakpointConfig;
  value: string;
  label: string;
  description: string;
  aliases?: string[];
}

export interface BreakpointParseResult {
  breakpoints: BreakpointConfig[];
  errors: string[];
  warnings: string[];
  expansions: Array<{ input: string; outputs: BreakpointConfig[] }>;
}

const phaseBreakpointDefinitions: BreakpointDefinition[] = [
  {
    config: { type: 'phase', id: 'afterSourcePrep' },
    value: 'afterSourcePrep',
    label: 'After Source Prep',
    description:
      'Pause once sources are unpacked, hooks ran, and postProcessApplicationSource completed.',
  },
  {
    config: { type: 'phase', id: 'beforeInstall' },
    value: 'beforeInstall',
    label: 'Before Install',
    description: 'Pause immediately before dependency installation begins.',
  },
  {
    config: { type: 'phase', id: 'afterInstall' },
    value: 'afterInstall',
    label: 'After Install',
    description:
      'Pause after dependencies and plugins install and before resource writes.',
  },
  {
    config: { type: 'phase', id: 'beforePack' },
    value: 'beforePack',
    label: 'Before Pack',
    description:
      'Pause after Electron Builder configuration is ready but before Packager.build() executes.',
  },
  {
    config: { type: 'phase', id: 'afterPack' },
    value: 'afterPack',
    label: 'After Pack',
    description: 'Pause once packaging completes and artifacts exist locally.',
  },
  {
    config: { type: 'phase', id: 'beforeUpload' },
    value: 'beforeUpload',
    label: 'Before Upload',
    description:
      'Pause when manifests and metadata are generated before uploads start.',
  },
  {
    config: { type: 'phase', id: 'afterUpload' },
    value: 'afterUpload',
    label: 'After Upload',
    description:
      'Pause after uploads finish but before CDN invalidation or bookkeeping.',
  },
  {
    config: { type: 'phase', id: 'beforeFinalize' },
    value: 'beforeFinalize',
    label: 'Before Finalize',
    description:
      'Pause prior to sendBuildEvent.done() and cleanup completing the build.',
  },
];

const hookBreakpointDefinitions: BreakpointDefinition[] = [
  {
    config: {
      type: 'hook',
      id: 'todesktop:beforeInstall',
      position: 'before',
    },
    value: 'hook:todesktop:beforeInstall:before',
    label: 'Before todesktop:beforeInstall',
    description: 'Pause just before the todesktop:beforeInstall hook runs.',
    aliases: ['todesktop:beforeInstall.before'],
  },
  {
    config: {
      type: 'hook',
      id: 'todesktop:beforeInstall',
      position: 'after',
    },
    value: 'hook:todesktop:beforeInstall:after',
    label: 'After todesktop:beforeInstall',
    description: 'Pause immediately after the todesktop:beforeInstall hook.',
    aliases: ['todesktop:beforeInstall.after'],
  },
  {
    config: {
      type: 'hook',
      id: 'todesktop:beforeBuild',
      position: 'before',
    },
    value: 'hook:todesktop:beforeBuild:before',
    label: 'Before todesktop:beforeBuild',
    description: 'Pause before the todesktop:beforeBuild hook executes.',
    aliases: ['todesktop:beforeBuild.before'],
  },
  {
    config: {
      type: 'hook',
      id: 'todesktop:beforeBuild',
      position: 'after',
    },
    value: 'hook:todesktop:beforeBuild:after',
    label: 'After todesktop:beforeBuild',
    description: 'Pause after the todesktop:beforeBuild hook.',
    aliases: ['todesktop:beforeBuild.after'],
  },
  {
    config: {
      type: 'hook',
      id: 'todesktop:afterPack',
      position: 'before',
    },
    value: 'hook:todesktop:afterPack:before',
    label: 'Before todesktop:afterPack',
    description: 'Pause prior to the todesktop:afterPack hook.',
    aliases: ['todesktop:afterPack.before'],
  },
  {
    config: {
      type: 'hook',
      id: 'todesktop:afterPack',
      position: 'after',
    },
    value: 'hook:todesktop:afterPack:after',
    label: 'After todesktop:afterPack',
    description: 'Pause after the todesktop:afterPack hook completes.',
    aliases: ['todesktop:afterPack.after'],
  },
];

const breakpointDefinitions: BreakpointDefinition[] = [
  ...phaseBreakpointDefinitions,
  ...hookBreakpointDefinitions,
];

const hookNames: HookBreakpointName[] = [
  'todesktop:beforeInstall',
  'todesktop:beforeBuild',
  'todesktop:afterPack',
];

const aliasMap: Record<string, BreakpointDefinition[]> = (() => {
  const map: Record<string, BreakpointDefinition[]> = {};
  for (const definition of breakpointDefinitions) {
    const aliases = new Set<string>([
      definition.value,
      ...(definition.aliases || []),
    ]);

    if (definition.config.type === 'hook') {
      const hookAlias = `${definition.config.id}:${definition.config.position}`;
      aliases.add(hookAlias);
    }

    for (const alias of aliases) {
      if (!map[alias]) {
        map[alias] = [];
      }
      map[alias].push(definition);
    }
  }

  return map;
})();

const allSuggestionTokens = Array.from(
  new Set([
    ...Object.keys(aliasMap),
    'hook:todesktop:beforeInstall:*',
    'hook:todesktop:beforeBuild:*',
    'hook:todesktop:afterPack:*',
  ]),
);

function canonicalKey(config: BreakpointConfig): string {
  if (config.type === 'phase') {
    return `phase:${config.id}`;
  }

  return `hook:${config.id}:${config.position}`;
}

function levenshtein(a: string, b: string): number {
  if (a === b) {
    return 0;
  }
  if (!a.length) {
    return b.length;
  }
  if (!b.length) {
    return a.length;
  }

  const matrix = Array.from({ length: a.length + 1 }, () =>
    Array(b.length + 1).fill(0),
  );

  for (let i = 0; i <= a.length; i += 1) {
    matrix[i][0] = i;
  }
  for (let j = 0; j <= b.length; j += 1) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= a.length; i += 1) {
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost,
      );
    }
  }

  return matrix[a.length][b.length];
}

function expandWildcardHook(
  hookName: HookBreakpointName,
): BreakpointDefinition[] {
  return hookBreakpointDefinitions.filter(
    (definition) => definition.config.id === hookName,
  );
}

export function parseBreakpointList(input: string): BreakpointParseResult {
  const tokens = input
    .split(',')
    .map((token) => token.trim())
    .filter(Boolean);

  const seen = new Set<string>();
  const breakpoints: BreakpointConfig[] = [];
  const errors: string[] = [];
  const warnings: string[] = [];
  const expansions: BreakpointParseResult['expansions'] = [];

  const pushDefinition = (definition: BreakpointDefinition, source: string) => {
    const key = canonicalKey(definition.config);
    if (seen.has(key)) {
      warnings.push(
        `Duplicate breakpoint "${definition.value}" ignored (from "${source}").`,
      );
      return;
    }
    seen.add(key);
    breakpoints.push(definition.config);
  };

  const suggest = (token: string): string | null => {
    let best: { token: string; distance: number } | null = null;
    for (const candidate of allSuggestionTokens) {
      const distance = levenshtein(token, candidate);
      if (!best || distance < best.distance) {
        best = { token: candidate, distance };
      }
    }
    if (best && best.distance <= Math.max(2, Math.floor(token.length / 2))) {
      return best.token;
    }
    return null;
  };

  for (const token of tokens) {
    if (token.startsWith('hook:') && token.endsWith(':*')) {
      const hookNameCandidate = token.slice(5, -2);
      if (!isHookBreakpointName(hookNameCandidate)) {
        const suggestion = suggest(token);
        errors.push(
          suggestion
            ? `Unknown breakpoint "${token}". Did you mean "${suggestion}"?`
            : `Unknown breakpoint "${token}".`,
        );
        continue;
      }
      const definitions = expandWildcardHook(hookNameCandidate);

      expansions.push({
        input: token,
        outputs: definitions.map((definition) => definition.config),
      });

      for (const definition of definitions) {
        pushDefinition(definition, token);
      }
      continue;
    }

    if (token.endsWith('.*')) {
      const hookNameCandidate = token.substring(0, token.length - 2);
      if (isHookBreakpointName(hookNameCandidate)) {
        const definitions = expandWildcardHook(hookNameCandidate);
        expansions.push({
          input: token,
          outputs: definitions.map((definition) => definition.config),
        });
        for (const definition of definitions) {
          pushDefinition(definition, token);
        }
        continue;
      }
    }

    const definitions = aliasMap[token];
    if (!definitions || definitions.length === 0) {
      const suggestion = suggest(token);
      errors.push(
        suggestion
          ? `Unknown breakpoint "${token}". Did you mean "${suggestion}"?`
          : `Unknown breakpoint "${token}".`,
      );
      continue;
    }

    if (definitions.length > 1) {
      expansions.push({
        input: token,
        outputs: definitions.map((definition) => definition.config),
      });
    }

    for (const definition of definitions) {
      pushDefinition(definition, token);
    }
  }

  return { breakpoints, errors, warnings, expansions };
}

export function listBreakpointDefinitions(): BreakpointDefinition[] {
  return breakpointDefinitions.slice();
}

export function formatBreakpoint(config: BreakpointConfig): string {
  if (config.type === 'phase') {
    return config.id;
  }
  return `hook:${config.id}:${config.position}`;
}

function isHookBreakpointName(value: string): value is HookBreakpointName {
  return (hookNames as string[]).includes(value);
}
